import { useState } from 'react';
import {
  clearStoredToken,
  createFolder,
  fetchGoogleUserInfo,
  findFolderByName,
  getValidToken,
  requestGoogleToken,
  revokeGoogleToken,
  validateFolder,
  type StorageConfig,
} from '@skjv/storage';

const SKJV_FOLDER_NAME = 'SKJV';

interface UseGoogleDriveConnectArgs {
  clientId: string;
  config: StorageConfig;
  applyConfig: (next: StorageConfig) => void;
}

interface GoogleDriveConnectApi {
  isConnected: boolean;
  needsFolderCreation: boolean;
  statusMessage: string | null;
  isWorking: boolean;
  /** Email Google would return for the current session (refreshed before sync). */
  liveEmail: string | null;
  setConnected: (v: boolean) => void;
  setStatusMessage: (v: string | null) => void;
  handleConnect: () => Promise<void>;
  handleCreateFolder: () => Promise<void>;
  handleDisconnect: () => Promise<void>;
  /** Validate the cached SKJV root id; if it's gone or trashed, re-find/create
   *  silently. Safe to call before every sync. */
  ensureRootFolderFresh: () => Promise<void>;
  /** Return the email of the currently-active Google session, or null. */
  fetchLiveEmail: () => Promise<string | null>;
}

export function useGoogleDriveConnect(args: UseGoogleDriveConnectArgs): GoogleDriveConnectApi {
  const { clientId, config, applyConfig } = args;
  const driveCfg = config.providers['google-drive'];

  const [isConnected, setConnected] = useState(false);
  const [needsFolderCreation, setNeedsFolderCreation] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isWorking, setWorking] = useState(false);
  const [liveEmail, setLiveEmail] = useState<string | null>(null);

  const saveFolderAndConnect = (folderId: string, email?: string): void => {
    applyConfig({
      ...config,
      providers: {
        ...config.providers,
        'google-drive': {
          ...driveCfg,
          enabled: true,
          clientId,
          folderId,
          email: email ?? driveCfg.email,
        },
      },
    });
    setConnected(true);
    setNeedsFolderCreation(false);
    if (email) setLiveEmail(email);
  };

  const resolveFolder = async (accessToken: string): Promise<void> => {
    const userInfo = await fetchGoogleUserInfo(accessToken);
    const email = userInfo?.email;
    let folderId = driveCfg.folderId;
    if (folderId) {
      const ok = await validateFolder(accessToken, folderId);
      if (!ok) folderId = undefined;
    }
    if (!folderId) {
      const found = await findFolderByName(accessToken, SKJV_FOLDER_NAME);
      if (found) {
        folderId = found;
      } else {
        setNeedsFolderCreation(true);
        setStatusMessage(`No "${SKJV_FOLDER_NAME}" folder found. Create one to finish setup.`);
        return;
      }
    }
    saveFolderAndConnect(folderId, email);
    setStatusMessage(email ? `Connected as ${email}.` : 'Connected to Google Drive.');
  };

  const handleConnect = async (): Promise<void> => {
    if (!clientId) {
      setStatusMessage('Google Drive is not configured for this build.');
      return;
    }
    setWorking(true);
    setStatusMessage(null);
    setNeedsFolderCreation(false);
    // Wipe any stale token so consent runs cleanly with the current scope.
    clearStoredToken();
    try {
      const token = await requestGoogleToken(clientId, driveCfg.scope, { prompt: 'consent' });
      await resolveFolder(token.accessToken);
    } catch (err) {
      setStatusMessage(`Connection failed: ${(err as Error).message}`);
    } finally {
      setWorking(false);
    }
  };

  const handleCreateFolder = async (): Promise<void> => {
    setWorking(true);
    setStatusMessage(null);
    try {
      const token = await getValidToken(clientId, driveCfg.scope);
      if (!token) throw new Error('Not signed in to Google');
      const userInfo = await fetchGoogleUserInfo(token.accessToken);
      const folderId = await createFolder(token.accessToken, SKJV_FOLDER_NAME);
      saveFolderAndConnect(folderId, userInfo?.email);
      setStatusMessage(`Created "${SKJV_FOLDER_NAME}" folder in your Google Drive.`);
    } catch (err) {
      setStatusMessage(`Folder creation failed: ${(err as Error).message}`);
    } finally {
      setWorking(false);
    }
  };

  const ensureRootFolderFresh = async (): Promise<void> => {
    if (!driveCfg.enabled || !clientId) return;
    const token = await getValidToken(clientId, driveCfg.scope);
    if (!token) return;
    if (driveCfg.folderId) {
      const stillThere = await validateFolder(token.accessToken, driveCfg.folderId);
      if (stillThere) return;
    }
    // Cached root is gone or trashed — re-find or recreate silently.
    let folderId = await findFolderByName(token.accessToken, SKJV_FOLDER_NAME);
    if (!folderId) folderId = await createFolder(token.accessToken, SKJV_FOLDER_NAME);
    applyConfig({
      ...config,
      providers: {
        ...config.providers,
        'google-drive': { ...driveCfg, folderId },
      },
    });
  };

  const handleDisconnect = async (): Promise<void> => {
    setWorking(true);
    try {
      const token = await getValidToken(clientId, driveCfg.scope);
      if (token) await revokeGoogleToken(token.accessToken);
      clearStoredToken();
      applyConfig({
        ...config,
        providers: {
          ...config.providers,
          'google-drive': { ...driveCfg, enabled: false, email: undefined },
        },
      });
      setConnected(false);
      setNeedsFolderCreation(false);
      setLiveEmail(null);
      setStatusMessage('Disconnected from Google Drive.');
    } finally {
      setWorking(false);
    }
  };

  const fetchLiveEmail = async (): Promise<string | null> => {
    if (!driveCfg.enabled || !clientId) return null;
    const token = await getValidToken(clientId, driveCfg.scope);
    if (!token) return null;
    const userInfo = await fetchGoogleUserInfo(token.accessToken);
    const email = userInfo?.email ?? null;
    setLiveEmail(email);
    return email;
  };

  return {
    isConnected,
    needsFolderCreation,
    statusMessage,
    isWorking,
    liveEmail,
    setConnected,
    setStatusMessage,
    handleConnect,
    handleCreateFolder,
    handleDisconnect,
    ensureRootFolderFresh,
    fetchLiveEmail,
  };
}
